import { ChangeEvent, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Helmet } from "react-helmet-async";
import * as Yup from "yup";
import { MultiValue, SingleValue } from "react-select";
import { AppDispatch } from "../../../redux/store";
import PermissionGate from "../../../core/layouts/permission-gate";
import ToastComponent from "../../../components/toast";
import StyledSelect from "../../../components/styled-select";
import { OptionValue } from "../../../types/component";
import Input from "../../../components/input";
import Button from "../../../components/button";
import {
  getErrorState,
  setBreadcrumbState,
} from "../../../redux/features/common-slice";
import {
  UserResponse,
  allUsersStateAction,
  getAllUsersState,
} from "../../../redux/features/user-slice";
import paths from "../../../navigation/paths";
import { Formik } from "formik";
import { adminCat } from "../../../core/utils/constants";
import { UserRole } from "../../../redux/features/user-slice";
import { isSelectSingleValue } from "../../../core/utils";
import {
  getCreateCustomerState,
  CreateCustomerRequest,
  createCustomerStateAction,
  clearCreateCustomerState,
  CustomerStatus,
} from "../../../redux/features/customer-slice";

export default function AddCustomerPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const createCustomerState = useSelector(getCreateCustomerState);
  const errorState = useSelector(getErrorState);
  const allUsersState = useSelector(getAllUsersState);

  const handleSuccessToast = useCallback(
    (message: string, clearAction: any) => {
      toast(message, { type: "success" });
      setTimeout(() => {
        dispatch(clearAction());
      }, 2000);
    },
    [dispatch]
  );

  const submitCreateCustomer = async (values: CreateCustomerRequest) => {
    await dispatch(createCustomerStateAction(values));
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      await dispatch(
        allUsersStateAction({
          teamIds: [],
          roles: [
            UserRole.Admin,
            UserRole.Supervisor,
            UserRole.Manager,
            UserRole.Leader,
            UserRole.Contributor,
            UserRole.Member,
            UserRole.CustomerSupport,
            UserRole.Trainer,
          ],
        })
      );
      dispatch(
        setBreadcrumbState([
          { id: paths.CUSTOMERS, path: null, title: "Customers" },
          {
            id: "addCustomer",
            path: null,
            title: "Add Customer",
          },
        ])
      );
    };
    fetchInitialData();
  }, [dispatch]);

  useEffect(() => {
    console.log(createCustomerState)
    if (createCustomerState.data && createCustomerState.data.success) {
      handleSuccessToast(
        createCustomerState.data.message,
        clearCreateCustomerState
      );
      setTimeout(() => {
        navigate(paths.CUSTOMERS, { replace: true });
      }, 2000);
    }
  }, [createCustomerState, handleSuccessToast, navigate]);

  useEffect(() => {
    if (errorState) {
      if (errorState.redirect) {
        navigate(errorState.redirect.destination, {
          replace: errorState.redirect.permanent,
        });
        return;
      }
      toast(errorState.message, { type: "error" });
    }
  }, [errorState, navigate]);

  return (
    <PermissionGate
      categories={[adminCat.id]}
      roles={[UserRole.Admin, UserRole.Trainer]}
    >
      <Helmet title="Add Customer" />
      <Formik
        initialValues={
          {
            name: "",
            email: "",
            status: CustomerStatus.Active,
            userIds: [],
          } as CreateCustomerRequest
        }
        validationSchema={Yup.object().shape({
          name: Yup.string().required("Name is required"),
          email: Yup.string().email("Must be a valid email").max(50).optional(),
          status: Yup.string()
            .oneOf(Object.values(CustomerStatus))
            .required("Status is required"),
          userIds: Yup.array().of(Yup.string()).optional(),
        })}
        onSubmit={(values, { setSubmitting }) => {
          setSubmitting(false);
          submitCreateCustomer(values);
        }}
      >
        {({
          values,
          errors,
          handleChange,
          handleSubmit,
          isSubmitting,
          setValues,
          dirty,
        }) => (
          <form
            noValidate
            autoComplete="off"
            onSubmit={handleSubmit}
            className="h-full overflow-y-auto"
          >
            <div
              className="h-full grid grid-cols-1 gap-8 overflow-y-scroll"
              style={{
                height: "calc(100% - 56px)",
              }}
            >
              <div className="space-y-4">
                <Input
                  label="Name *"
                  type="text"
                  id="name"
                  value={values.name}
                  onChange={handleChange}
                  errorText={errors.name}
                />
                <Input
                  label="Email"
                  type="email"
                  id="email"
                  value={values.email}
                  onChange={handleChange}
                  errorText={errors.email}
                />
                <div className="flex flex-col">
                  <label className="text-xs text-text-title">Status *</label>
                  <StyledSelect
                    options={Object.values(CustomerStatus).map((status) => ({
                      value: status,
                      label: status.charAt(0).toUpperCase() + status.slice(1),
                    }))}
                    isClearable={false}
                    menuPosition="fixed"
                    value={
                      values.status
                        ? {
                            value: values.status,
                            label:
                              values.status.charAt(0).toUpperCase() +
                              values.status.slice(1),
                          }
                        : { value: "", label: "Select..." }
                    }
                    onChange={(
                      value: SingleValue<OptionValue> | MultiValue<OptionValue>
                    ) => {
                      if (value && isSelectSingleValue(value)) {
                        setValues({
                          ...values,
                          status: value.value as CustomerStatus,
                        });
                      }
                    }}
                  />
                  {errors.status && (
                    <p className="text-error-main text-xs">{errors.status}</p>
                  )}
                </div>
                <div className="flex flex-col">
                  <label className="text-xs text-text-title">
                    Associated Users
                  </label>
                  <StyledSelect
                    multiple
                    loading={allUsersState.loading}
                    options={allUsersState.data.map((user: UserResponse) => ({
                      value: user.id,
                      label: user.name,
                    }))}
                    menuPosition="fixed"
                    value={values.userIds?.map((id) => {
                      const user = allUsersState.data.find((u) => u.id === id);
                      return {
                        value: id,
                        label: user ? user.name : "Select...",
                      };
                    })}
                    onChange={(
                      value: SingleValue<OptionValue> | MultiValue<OptionValue>
                    ) => {
                      if (Array.isArray(value)) {
                        setValues({
                          ...values,
                          userIds: value.map((v) => v.value as string),
                        });
                      } else {
                        setValues({ ...values, userIds: [] });
                      }
                    }}
                  />
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-4 pt-4">
              <Button
                label="Submit"
                type="submit"
                loading={createCustomerState.loading}
                disabled={
                  Array.isArray(errors) ||
                  Object.values(errors).toString() != "" ||
                  createCustomerState.loading
                }
              />
            </div>
          </form>
        )}
      </Formik>
      <ToastComponent id="addCustomer" />
    </PermissionGate>
  );
}
